<template>
  <header class="site-header" @mouseleave="dropdownOpen = false">
    <div class="logo-container">
      <img :src="logoPath" alt="Creston Group Logo" class="logo" />
    </div>

    <nav class="navigation">
      <ul>
        <li>
          <router-link
            to="/"
            class="nav-link"
            :class="{ active: isActive('/') }"
            @click="navTrack('Header Navigation', 'home')"
          >
            {{ menuTexts.home }}
          </router-link>
        </li>
        <li>
          <router-link
            to="/about"
            class="nav-link"
            :class="{ active: isActive('/about') }"
            @click="navTrack('Header Navigation', 'about us')"
          >
            {{ menuTexts.aboutUs }}
          </router-link>
        </li>
        <li class="dropdown" @mouseenter="dropdownOpen = true">
          <router-link
            to="/core-offers"
            class="nav-link"
            :class="{ active: isActive('/core-offers') }"
            @click="navTrack('Header Navigation', 'Our Offers')"
          >
            {{ menuTexts.ourOffers }}
            <span class="arrow-down">&#9662;</span>
          </router-link>
          <ul v-show="dropdownOpen" class="submenu">
            <li>
              <router-link
                to="/core-offers/source-product"
                class="nav-link"
                :class="{ active: isActive('/core-offers/source-product') }"
                @click="navTrack('Header Navigation', 'source product')"
              >
                {{ menuTexts.sourcingIllustrations }}
              </router-link>
            </li>
          </ul>
        </li>
        <li>
          <router-link
            id="cta-contact-header"
            to="/contact-us"
            class="nav-link"
            :class="{ active: isActive('/contact-us') }"
            @click="handleContactClick"
          >
            {{ menuTexts.contactUs }}
          </router-link>
        </li>
      </ul>

      <div class="language-selector" @click="openDialog">
        <img
          :src="selectedLang.flag"
          :alt="`${selectedLang.name} Flag`"
          class="flag-icon"
        />
        <span class="language-text">{{ selectedLang.code.toUpperCase() }}</span>
        <span class="arrow-down">&#9662;</span>
      </div>
    </nav>

    <LanguageDialog
      v-model:visible="showDialog"
      :languages="languages"
      @select-language="handleSelectLanguage"
    />
  </header>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import siteLogo from '@/assets/logo.webp'
import LanguageDialog from '@/components/LanguageDialog.vue'
import { pushToDataLayer } from '../utils/gtm'
import { languages } from '@/utils/languages'
import { saveCTAInfo, CTA_CONFIG } from '../utils/ctaTracking'

const { locale, messages } = useI18n() 
const route = useRoute()

// 获取菜单文本
const menuTexts = computed(() => {
  return messages.value[locale.value]?.menu || {
    home: 'Home',
    ourOffers: 'Our Offers',
    coreOffers: 'Core Offers',
    sourcingIllustrations: 'Sourcing Illustrations',
    contactUs: 'Contact Us',
    aboutUs: 'About Us'
  };
});

const logoPath = ref(siteLogo)
const showDialog = ref(false)
const dropdownOpen = ref(false)

const isActive = (path) => {
  return route.path === path || route.path.startsWith(path + '/')
}

const openDialog = () => {
  showDialog.value = true
}

const selectedLang = ref(
  languages.find(l => l.code === locale.value) || languages[0]
)

const handleSelectLanguage = (lang) => {
  const oldLang = locale.value
  locale.value = lang.code
  selectedLang.value = lang
  
  localStorage.setItem('language', lang.code)
  localStorage.setItem('language_manual_selection', 'true')
  localStorage.setItem('language_selection_date', new Date().toISOString())


  pushToDataLayer('language_change', {
    from_language: oldLang,
    to_language: lang.code,
    selection_method: 'manual'
  })
}

const navTrack = (category, label) => {
  pushToDataLayer('click', {
    category,
    label,
  })
}

const handleContactClick = () => {
  // 保存 CTA 信息到 sessionStorage
  saveCTAInfo(CTA_CONFIG.HEADER_CONTACT)
  navTrack('Header Navigation', 'Contact Us')
}
</script>

<style scoped>
.site-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #001f5a;
  padding: 0;
  color: white;
  height: 60px;
  position: relative;
}

.logo-container {
  display: flex;
  height: 100%;
}

.logo {
  height: 60px;
  width: auto;
  margin-right: 10px;
}

.navigation {
  display: flex;
  height: 50px;
  margin-right: 10px;
  background: rgba(203, 203, 203, 0.7);
  border-radius: 25px;
  backdrop-filter: blur(13px);
  padding-left: 40px;
  padding-right: 8px;
  align-items: center;
}

.navigation ul {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  align-items: center;
}

.navigation li {
  margin: 0 20px;
  position: relative;
}

.nav-link {
  text-decoration: none;
  color: #000;
  font-weight: bold;
  font-size: 18px;
  padding: 10px 5px;
  cursor: pointer;
}

.nav-link.active,
.nav-link:hover {
  color: #0d0971;
}

.language-selector {
  display: flex;
  align-items: center;
  background-color: #001f5a;
  color: white;
  padding: 0px 4px;
  border-radius: 23px;
  margin-left: 20px;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  height: 46px;
  width: 100px;
}

.flag-icon {
  height: 40px;
  width: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.language-text {
  margin: 0 10px;
}

/* Submenu styles */
.submenu {
  position: absolute;
  top: 150%;
  left: 0;
  background: rgba(203, 203, 203, 0.7);
  color: #000;
  border-radius: 10px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(12px);
}

.submenu li {
  padding: 10px 0px;
  white-space: nowrap;
}

.submenu .nav-link {
  font-weight: normal;
  padding: 0;
  font-size: 18px;
  display: block;
  color: #000000;
  font-weight: bold;
}

.submenu .nav-link:hover {
  color: #0d0971;
}
</style>
